import { For, Show } from 'solid-js';
import { Badge } from '~/components/primitives/badge';
import { Bar } from '~/components/primitives/bar';
import { Glyph } from '~/components/glyph';
import { deck } from '~/lib/deck';
import { COUNTER_VERB } from '~/lib/tools';
import type { DeckStats, IceType, Tone } from '~/types/game';

const STATS: Array<{ key: keyof DeckStats; label: string; abbr: string }> = [
  { key: 'processing', label: 'PROCESSING', abbr: 'P' },
  { key: 'intrusion', label: 'INTRUSION', abbr: 'I' },
  { key: 'stealth', label: 'STEALTH', abbr: 'S' },
];

const COUNTER_TONE: Record<IceType, Tone> = {
  WALL: 'warn',
  CIPHER: 'data',
  DAEMON: 'drone',
};

/** DECK screen — the loadout builder. Shows the deck's three stats as a
 *  deployment budget, the owned toolset, and the equipped loadout, enforcing
 *  and visualizing the capacity constraint. It assembles/validates a loadout;
 *  it does not resolve a hack (step 3). */
export function Deck() {
  const totalStrength = () => deck.equippedTools().reduce((s, t) => s + t.strength, 0);

  return (
    <>
      <div
        style={{
          padding: '8px 12px',
          'border-bottom': '1px solid var(--line-faint)',
          display: 'flex',
          'align-items': 'center',
          gap: '8px',
        }}
      >
        <Glyph name="deck" size={16} color="var(--action)" />
        <span class="t-mono" style={{ 'font-size': '14px', color: 'var(--fg)' }}>
          LOADOUT
        </span>
        <span class="t-micro">/ DECK · KOMODO MK-IV</span>
        <span style={{ flex: 1 }} />
        <Show when={deck.isValid()} fallback={<Badge tone="hostile">OVER BUDGET</Badge>}>
          <Badge tone="action">LOADOUT VALID</Badge>
        </Show>
      </div>

      {/* Deployment budget — one bar per deck stat, over-budget in hostile. */}
      <div style={{ padding: '12px', 'border-bottom': '1px solid var(--line-faint)' }}>
        <div class="t-label" style={{ 'margin-bottom': '8px' }}>
          DEPLOYMENT BUDGET
        </div>
        <div style={{ display: 'flex', 'flex-direction': 'column', gap: '8px' }}>
          <For each={STATS}>
            {(s) => {
              const used = () => deck.loadoutCost()[s.key];
              const max = () => deck.stats()[s.key];
              const over = () => used() > max();
              return (
                <div>
                  <div style={{ display: 'flex', 'align-items': 'baseline', 'margin-bottom': '3px' }}>
                    <span class="t-micro">{s.label}</span>
                    <span style={{ flex: 1 }} />
                    <span
                      class="t-mono t-tab"
                      style={{
                        'font-size': '10px',
                        color: over() ? 'var(--hostile)' : 'var(--action)',
                      }}
                    >
                      {used()} <span style={{ color: 'var(--fg-dim)' }}>/ {max()}</span>
                    </span>
                  </div>
                  <Bar pct={Math.min(100, (used() / max()) * 100)} tone={over() ? 'hostile' : 'action'} />
                </div>
              );
            }}
          </For>
        </div>
      </div>

      {/* Equipped loadout summary. */}
      <div
        style={{
          padding: '8px 12px 4px',
          display: 'flex',
          'align-items': 'baseline',
          gap: '8px',
        }}
      >
        <div class="t-label">LOADOUT</div>
        <span class="t-micro">/ {deck.equippedTools().length} EQUIPPED</span>
        <span style={{ flex: 1 }} />
        <span class="t-micro">
          STR <span style={{ color: 'var(--action)' }}>{totalStrength()}</span>
        </span>
      </div>

      {/* Owned tools — tap to equip / unequip. */}
      <div style={{ padding: '4px 12px 12px' }}>
        <div class="t-label" style={{ 'margin-bottom': '6px' }}>
          OWNED TOOLS / {deck.ownedTools().length}
        </div>
        <div style={{ display: 'flex', 'flex-direction': 'column', gap: '6px' }}>
          <For each={deck.ownedTools()}>
            {(t) => {
              const equipped = () => deck.isEquipped(t.id);
              return (
                <div
                  class="panel"
                  onClick={() => deck.toggle(t.id)}
                  style={{
                    padding: '8px 10px',
                    cursor: 'pointer',
                    'border-color': equipped() ? 'var(--action-line)' : undefined,
                    background: equipped() ? 'var(--action-bg)' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
                    <Glyph
                      name={equipped() ? 'chip' : 'plus'}
                      size={12}
                      color={equipped() ? 'var(--action)' : 'var(--fg-dim)'}
                    />
                    <span class="t-mono" style={{ 'font-size': '12px', color: 'var(--fg)' }}>
                      {t.name}
                    </span>
                    <span style={{ flex: 1 }} />
                    <Badge tone={COUNTER_TONE[t.counters]}>{COUNTER_VERB[t.counters]}</Badge>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      'align-items': 'center',
                      gap: '12px',
                      'margin-top': '6px',
                    }}
                  >
                    <span class="t-micro" style={{ color: 'var(--fg-dim)' }}>
                      {t.desc}
                    </span>
                    <span style={{ flex: 1 }} />
                    <span class="t-micro">
                      COST{' '}
                      <span style={{ color: 'var(--data)' }}>
                        P{t.cost.processing} I{t.cost.intrusion} S{t.cost.stealth}
                      </span>
                    </span>
                    <span class="t-micro">
                      STR <span style={{ color: 'var(--action)' }}>{t.strength}</span>
                    </span>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      </div>
    </>
  );
}
